import { AccessConfig, AccessContext, AccessPredicate, PermissionFlag } from './access'

/**
 * Permission rules ported from the legacy `app/` (roleService + leftMenu +
 * ribbon menus + administration panel). This is the single reference for
 * "who can do what" so gates across the new `modules/` stay consistent with
 * the old behaviour.
 *
 * Global role semantics (from `app/services/local/roleService.ts`):
 *  - isAdmin       — globalAdmin. Bypasses admin-only surfaces, but the legacy
 *                    app *excluded* admins from the per-company operational
 *                    modules (driver/vehicle/import/export/printout/timetable).
 *                    isDriver()/isManager() are always false for any admin.
 *  - isPaymentAdmin / isCompanyAdmin / isDemoCompanyAdmin — billing / company /
 *                    demo administrators; mostly gate admin-panel tabs.
 *  - isManager(c)  — has any of canEditCompany|canCreateCompany|canRemoveCompany|
 *                    canEditUser|canCreateUser|canRemoveUser and is not an admin.
 *  - isDriver(c)   — has an `isDriver` role and is not an admin. Heavily
 *                    restricted self-service user.
 *  - service company — a company property (not a role) that hides operational
 *                    modules and surfaces the contractors/companies admin area.
 */

/** Operational capability flags — at least one marks a "real" back-office user. */
export const OPERATIONAL_FLAGS: PermissionFlag[] = [
  'canViewActivities',
  'canEditActivities',
  'canViewBillingData',
  'canEditBillingData',
  'canViewDelegations',
  'canEditDelegations',
  'canViewAlerts',
  'canEditAlerts',
  'canImportFiles',
  'canExportData',
  'canGeneratePrintouts',
  'canViewConfiguration',
  'canAddDriver',
]

/** Company/user-management flags — the backing set behind `isManager`. */
export const MANAGEMENT_FLAGS: PermissionFlag[] = [
  'canEditCompany',
  'canCreateCompany',
  'canRemoveCompany',
  'canEditUser',
  'canCreateUser',
  'canRemoveUser',
]

/**
 * Declarative description of a gated area, keyed by feature. `permissions` is
 * the any-of flag set the legacy module gated on; `excludeDrivers` / `adminVisible`
 * capture the role rules. Use these constants when wiring guards into the
 * corresponding modules so the logic isn't re-derived per call site.
 *
 * (Reference for the operational modules — the HOME module uses the predicates
 * in {@link homeAccess} below.)
 */
export const MODULE_ACCESS: Record<
  string,
  { permissions: PermissionFlag[]; excludeDrivers?: boolean; adminVisible?: boolean }
> = {
  drivers: { permissions: ['canViewActivities', 'canEditActivities'], adminVisible: false },
  vehicles: {
    permissions: ['canViewActivities', 'canEditActivities'],
    excludeDrivers: true,
    adminVisible: false,
  },
  imports: { permissions: ['canImportFiles'], adminVisible: false },
  exports: { permissions: ['canExportData'], adminVisible: false },
  printouts: { permissions: ['canGeneratePrintouts'], adminVisible: false },
  timetable: { permissions: ['canViewAlerts', 'canEditAlerts'], adminVisible: false },
  settings: { permissions: ['canViewConfiguration'] },
  addDriverOrVehicle: { permissions: ['canAddDriver'] },
}

/**
 * Access predicates for the HOME module pages.
 *
 * Derived from the legacy app: the dashboard landing was never gated by a
 * single permission (companySelection routed every member to it), so we keep
 * the page open to any company member and gate individual cards by their own
 * domain flags. The creator wizard walks the operational surfaces, so it is
 * restricted to non-driver members who hold at least one operational flag
 * (mirroring the old left-nav `showList` rule). Profile and the company
 * switcher are identity-level and open to all authenticated members.
 */
export const homeAccess = {
  /** Dashboard landing — any admin or any company member. */
  dashboard: ((ctx: AccessContext): boolean =>
    ctx.isAdmin() || ctx.isPaymentAdmin() || ctx.hasAnyRole) as AccessPredicate,

  /** Company creation / setup wizard — back-office members, never pure drivers. */
  creator: ((ctx: AccessContext): boolean =>
    ctx.isAdmin() ||
    ctx.isPaymentAdmin() ||
    ctx.isManager() ||
    (!ctx.isDriver() && ctx.canAny(OPERATIONAL_FLAGS))) as AccessPredicate,
}

/**
 * Access rules for the management modules in the left sidebar. Each is an
 * any-of permission set with the default admin bypass (global/payment admins
 * pass; per-company members pass only with the right role flags). A pure driver
 * holds none of these flags, so the no-permission screen is shown.
 *
 * Flag choices mirror `useCompanyPermissions` and the legacy admin-panel/nav
 * rules:
 *  - settings      → canViewConfiguration
 *  - users         → canManageUsers      (canEditUser | canCreateUser)
 *  - databases     → canManageDatabases  (canEditCompany) + canReadDatabaseLogin
 *  - addCompany    → canManageCompanies  (canCreateCompany | canEditCompany)
 *  - billing       → canViewBillingData  | canEditBillingData
 *  - subscription  → billing or company management
 */
// Kierowca korzysta z modułów operacyjnych dla własnych danych bez flag (serwer: DriverPermissionValidator).
const flagOrDriver =
  (...flags: PermissionFlag[]): AccessPredicate =>
  (ctx: AccessContext) =>
    ctx.isAnyAdmin() || ctx.isDriver() || ctx.canAny(flags)

export const moduleAccess: Record<
  | 'settings'
  | 'users'
  | 'databases'
  | 'addCompany'
  | 'billing'
  | 'subscription'
  | 'imports'
  | 'exports'
  | 'printouts'
  | 'timetable',
  AccessConfig
> = {
  settings: { permissions: 'canViewConfiguration' },
  users: { permissions: ['canEditUser', 'canCreateUser'] },
  databases: { permissions: ['canEditCompany', 'canReadDatabaseLogin'] },
  addCompany: { permissions: ['canCreateCompany', 'canEditCompany'] },
  billing: { permissions: ['canViewBillingData', 'canEditBillingData'] },
  subscription: {
    permissions: ['canViewBillingData', 'canEditBillingData', 'canEditCompany'],
  },
  imports: { require: flagOrDriver('canImportFiles') },
  exports: { require: flagOrDriver('canExportData') },
  printouts: { require: flagOrDriver('canGeneratePrintouts') },
  timetable: { require: flagOrDriver('canViewAlerts', 'canEditAlerts') },
}
